import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import DOMPurify from 'dompurify'
import { privacy, terms, cookies, type LegalSection, type LegalBlock } from '../legal/legalContent'

export type LegalDoc = 'privacy' | 'terms' | 'cookies'

const API_BASE =
  ((import.meta as unknown as { env?: Record<string, string> }).env?.VITE_API_BASE) ||
  'https://api.bimauncle.com'

function docData(doc: LegalDoc) {
  if (doc === 'privacy') return privacy
  if (doc === 'terms') return terms
  return cookies
}

export function legalTitle(doc: LegalDoc): string {
  return docData(doc).title
}

function Block({ block }: { block: LegalBlock }) {
  if (block.type === 'p') return <p className="mb-3 text-ink-soft">{block.text}</p>
  if (block.type === 'ul')
    return (
      <ul className="mb-3 list-disc space-y-1 pl-5 text-ink-soft">
        {block.items.map((it, i) => (
          <li key={i}>{it}</li>
        ))}
      </ul>
    )
  return (
    <div className="mb-3 overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr>
            {block.head.map((h) => (
              <th
                key={h}
                className="border-b border-[var(--color-divider)] py-1.5 pr-3 text-left font-semibold"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {block.rows.map((row, ri) => (
            <tr key={ri}>
              {row.map((c, ci) => (
                <td
                  key={ci}
                  className="border-b border-[var(--color-divider)] py-1.5 pr-3 align-top text-ink-soft"
                >
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Renders a legal document from the CANONICAL single source
 * (src/legal/legalContent.ts, synced from frontend/src/config by
 * scripts/sync-legal.cjs at build time) so the marketing site, the app and the
 * web app show byte-identical Privacy / Terms text. Used by the Footer modal
 * and the standalone /privacy and /terms routes ({@link ./LegalPage}).
 */
export function LegalContent({ doc }: { doc: LegalDoc }) {
  const d = docData(doc)
  const [override, setOverride] = useState<{ version: string; html: string } | null>(null)

  useEffect(() => {
    let cancelled = false
    fetch(`${API_BASE}/v2/app/legal`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        const o = data && data[doc]
        if (!cancelled && o && typeof o.html === 'string' && o.html.trim()) setOverride(o)
      })
      .catch(() => {
        /* fall back to baked content */
      })
    return () => {
      cancelled = true
    }
  }, [doc])

  // Admin-published override (rich-text), sanitised before injection.
  if (override) {
    return (
      <>
        <p className="mb-4 text-sm font-medium text-ink">Version {override.version}</p>
        <div
          className="text-ink-soft [&_a]:text-brand [&_a]:underline [&_h1]:mb-2 [&_h1]:mt-4 [&_h1]:text-xl [&_h1]:font-bold [&_h2]:mb-2 [&_h2]:mt-4 [&_h2]:text-lg [&_h2]:font-bold [&_h3]:mb-2 [&_h3]:mt-4 [&_h3]:font-bold [&_li]:mb-1 [&_ol]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-3 [&_table]:mb-3 [&_table]:w-full [&_td]:border [&_td]:border-[var(--color-divider)] [&_td]:p-1.5 [&_th]:border [&_th]:border-[var(--color-divider)] [&_th]:p-1.5 [&_th]:text-left [&_ul]:mb-3 [&_ul]:list-disc [&_ul]:pl-5"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(override.html) }}
        />
      </>
    )
  }

  return (
    <>
      <p className="mb-1 text-sm font-medium text-ink">{d.subtitle}</p>
      <p className="mb-6 text-xs text-ink-soft">{d.intro}</p>
      {d.sections.map((s: LegalSection, i: number) => (
        <section key={i} className="mb-7">
          <h3 className="mb-2 text-lg font-bold">{s.heading}</h3>
          {s.blocks.map((b, j) => (
            <Block key={j} block={b} />
          ))}
        </section>
      ))}
    </>
  )
}

type Props = {
  doc: LegalDoc | null
  onClose: () => void
}

export function LegalModal({ doc, onClose }: Props) {
  useEffect(() => {
    if (!doc) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', onKey)
    }
  }, [doc, onClose])

  if (!doc) return null

  const title = legalTitle(doc)

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className="flex h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-t-modal bg-white sm:h-[85vh] sm:rounded-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-[var(--color-divider)] px-6 py-4">
          <h2 className="text-xl font-bold tracking-tight">{title}</h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="rounded-pill p-2 text-ink-soft hover:bg-black/5"
          >
            <X size={22} />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-6 py-6 text-[15px] leading-relaxed text-ink">
          <LegalContent doc={doc} />
        </div>
      </div>
    </div>
  )
}
